import IBAN from 'iban'
import { z } from 'zod'

import { getIsRequiredSharedCapital } from '@features/invoices/utils/getIsRequiredSharedCapital'
import { getRegistrationNumberFieldInvalidFormatErrorMessage } from '@features/invoices/utils/getRegistrationNumberFieldInvalidFormatErrorMessage'
import { getVatNumberFieldInvalidFormatErrorMessage } from '@features/invoices/utils/getVatNumberFieldInvalidFormatErrorMessage'
import { MAX_COMPANY_NAME_LENGTH } from '@features/invoicingSettings/constants/maxCompanyNameLength'
import { FrenchLegalStatus } from '@features/invoicingSettings/enums/frenchLegalStatus'

import { getRegistrationNumberRegex, getVatNoRegex } from '../../../../../../../utils'
import { GetMissingCompanyDetailsFormSchema } from '../types/getMissingCompanyDetailsFormSchema'

export const getFrenchCompanyFormValidationSchema: GetMissingCompanyDetailsFormSchema = ({ countryId, t }) => {
  return z
    .object({
      addressLine2: z.string().optional(),
      autoCompletedFields: z.record(z.string(), z.boolean()).optional(),
      bank: z.string().optional(),
      companyAddress: z
        .string()
        .trim()
        .nonempty({
          message: t(
            'external_invoices.missing_company_compliance_details.modal.form.field.address.street_address_1.error.required',
          ),
        }),
      companyCity: z
        .string()
        .trim()
        .nonempty({
          message: t(
            'external_invoices.missing_company_compliance_details.modal.form.field.address.city.error.required',
          ),
        }),
      companyName: z
        .string()
        .trim()
        .min(1, {
          message: t(
            'external_invoices.missing_company_compliance_details.modal.form.field.company_name.error.required',
          ),
        })
        .max(MAX_COMPANY_NAME_LENGTH),
      companyZipCode: z
        .string()
        .trim()
        .nonempty({
          message: t(
            'external_invoices.missing_company_compliance_details.modal.form.field.address.pobox.error.required',
          ),
        }),
      iban: z
        .string()
        .trim()
        .transform((iban) => iban.replace(/\s/g, ''))
        .refine((iban) => !iban || IBAN.isValid(iban), {
          message: t('external_invoices.missing_company_compliance_details.modal.form.field.iban.error.invalid_format'),
        })
        .optional(),
      isSiretPendingAssignment: z.boolean().optional(),
      isVatExempted: z.boolean(),
      registrationNo: z
        .string()
        .trim()
        .refine((value) => !value || getRegistrationNumberRegex(countryId).test(value), {
          message: getRegistrationNumberFieldInvalidFormatErrorMessage(countryId, t),
        }),
      swift: z
        .string()
        .refine((swift) => !swift || (swift.length >= 8 && swift.length <= 11), {
          message: t(
            'external_invoices.missing_company_compliance_details.modal.form.field.swift_bic.error.invalid_format',
          ),
        })
        .optional(),
      vatNo: z.string().trim(),
      legalStatusCustom: z.string().optional(),
      legalStatus: z.nativeEnum(FrenchLegalStatus, {
        /* eslint-disable @typescript-eslint/naming-convention */
        required_error: t('settings.organization.organization_info.basic_info.legal_status.required'),
      }),
      sharedCapital: z.number().optional(),
    })
    .superRefine(
      (
        {
          isSiretPendingAssignment,
          isVatExempted,
          legalStatusCustom,
          legalStatus,
          registrationNo,
          sharedCapital,
          vatNo,
        },
        ctx,
      ) => {
        if (!registrationNo && !isSiretPendingAssignment) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'external_invoices.missing_company_compliance_details.modal.form.field.registration_no.error.required',
            ),
            path: ['registrationNo'],
          })
        }

        if (legalStatus === FrenchLegalStatus.Other && !legalStatusCustom) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('settings.organization.organization_info.basic_info.legal_status_custom.error'),
            path: ['legalStatusCustom'],
          })
        }

        if (getIsRequiredSharedCapital({ legalStatus, sharedCapital })) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'external_invoices.missing_company_compliance_details.modal.form.field.shared_capital.error.required',
            ),
            path: ['sharedCapital'],
          })
        }

        if (!isVatExempted) {
          if (!vatNo) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('external_invoices.missing_company_compliance_details.modal.form.field.vat_no.error.required'),
              path: ['vatNo'],
            })
          }

          if (vatNo && !getVatNoRegex(countryId).test(vatNo)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: getVatNumberFieldInvalidFormatErrorMessage(countryId, t),
              path: ['vatNo'],
            })
          }
        }
      },
    )
}
